import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet-async';

import RequestState from '../../../types/RequestState';
import ContentForState from '../../loader/ContentForState';
import OrderStore from '../../../store/OrderStore';
import { modelOf } from '../../../prop-types';
import ProductStore from '../../../store/ProductStore';
import Analytics from '../../../analytics/Analytics';
import { paramsToQueryIdentifier } from '../../../util/query';
import { createErrorModel } from '../../../util/error';
import globalTranslations from '../../../i18n/globalTranslations';
import ErrorHandler from '../../loader/ErrorHandler';
import Redirect302 from '../../route/Redirect302';
import CheckoutOrderConfirmation from '../CheckoutOrderConfirmation';
import * as queryString from '../../../util/queryString';
import HelperNavigation from '../../common/HelperNavigation';
import { Alert, Button, Row } from 'reactstrap';
import CustomCol from '../../bootstrap/CustomCol';
import PageTitle from '../../common/PageTitle';
import TrackingPixels from '../../../analytics/TrackingPixels';
import ShopAlike from '../../../analytics/ShopAlike';
import CurrencyStore from '../../../store/CurrencyStore';
import Icon from '../../common/Icon';
import ConfigStore from '../../../store/ConfigStore';
import AdZones from '../../../types/AdZones';
import ContentSlots from '../../ad/ContentSlots';

@observer
export class ThankYouPageContent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      productStoreError: null,
      renderTrackingPixels: false
    };
  }

  componentDidMount() {
    this.loadOrder();
  }

  loadOrder = () => {
    const { match, location, orderStore, configStore } = this.props;
    const { orderId, trackingCode } = match.params;
    const queryParams = queryString.parse(location.search);
    const merchantId = queryParams.merchantId;
    const orderParams = {
      orderId,
      trackingCode,
      merchantId
    };
    orderStore
      .loadCurrentOrderPostPurchase(orderParams)
      .then(() => {
        configStore.gtm.enabled && this.updateAnalyticsDataLayer();
        this.loadProductData();
      })
      .catch((error) => console.error(error));
  };

  updateAnalyticsDataLayer = () => {
    const { analytics, orderStore } = this.props;
    const { currentOrder } = orderStore;

    // Because the data array is not organized with keys or inside js objects, we have to assume that the correct object is at index 0.
    if (
      currentOrder &&
      currentOrder.shippingMethod &&
      window.dataLayer &&
      window.dataLayer.length > 0
    ) {
      const dimensionKey = 'shippingMethod';
      const dimensionValue = currentOrder.shippingMethod.name;
      analytics.updateDimension(dimensionKey, dimensionValue);
    }

    if (
      currentOrder &&
      currentOrder.paymentMethod &&
      window.dataLayer &&
      window.dataLayer.length > 0
    ) {
      const dimensionKey = 'paymentMethod';
      const dimensionValue = currentOrder.paymentMethod.name;
      analytics.updateDimension(dimensionKey, dimensionValue);
    }
  };

  loadProductData = () => {
    const { productStore } = this.props;
    productStore
      .loadProducts({
        ...this.getProductIdQuery(),
        includeChildProducts: 1
      })
      .then(() => {
        this.sendAnalyticsRequest();
        this.setupAnalyticsTrackingEvent();
      })
      .catch((error) => {
        this.setState(() => ({
          productStoreError: createErrorModel(error)
        }));
      });
  };

  sendAnalyticsRequest = () => {
    const { productStore, analytics, orderStore } = this.props;
    const { currentOrder } = orderStore;

    this.setState({
      renderTrackingPixels: !currentOrder.is_seen
    });

    if (currentOrder.is_seen) {
      return;
    }

    const productData = productStore.productQueryResults.get(
      this.getProductRequestIdentifier()
    ).data;

    analytics.purchase(currentOrder, productData);
  };

  setupAnalyticsTrackingEvent = () => {
    const { onOrderSuccess, orderStore } = this.props;
    const { currentOrder } = orderStore;

    onOrderSuccess && currentOrder && this.props.onOrderSuccess(currentOrder);
  };

  getProductIdQuery = () => {
    const { currentOrder } = this.props.orderStore;

    return {
      ids: currentOrder
        ? currentOrder.products.map((product) => product.product_id)
        : []
    };
  };

  getProductRequestIdentifier = () => {
    return paramsToQueryIdentifier({
      ...this.getProductIdQuery(),
      includeChildProducts: 1
    });
  };

  getProductRequestState = () => {
    const { productStore, orderStore } = this.props;

    if (!orderStore.currentOrder) {
      return RequestState.NONE;
    }

    return productStore.productQueryStates.get(
      this.getProductRequestIdentifier()
    );
  };

  getCurrentlyRunningRequestState = () => {
    const { orderStore } = this.props;
    const currentRequestState = [
      orderStore.currentOrderState,
      this.getProductRequestState()
    ].find((state) => state !== RequestState.LOADED);

    return currentRequestState || RequestState.LOADED;
  };

  getBreadcrumbsPath = () => {
    const { intl } = this.props;

    return [
      {
        text: intl.formatMessage(globalTranslations.checkoutTitle)
      },
      {
        text: intl.formatMessage({
          id: 'checkout.successfulPurchaseBreadcrumbName',
          defaultMessage: 'Thank You'
        })
      }
    ];
  };

  getError = () => {
    const { orderStore } = this.props;
    const { productStoreError } = this.state;

    const error = orderStore.lastCurrentOrderError || productStoreError;
    return error || null;
  };

  renderHtmlWidget = (widgetHtml) => {
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = widgetHtml;

    return (
      <Fragment>
        <Helmet>
          {[].map.call(doc.getElementsByTagName('script'), (el) => (
            <script>{el.textContent}</script>
          ))}
        </Helmet>
        <div dangerouslySetInnerHTML={{ __html: widgetHtml }} />
      </Fragment>
    );
  };

  renderErrorHandler = (props) => {
    const { configStore } = this.props;
    const { checkout } = configStore;
    switch (props.error.httpStatusCode) {
      case 401:
        return <Redirect302 to="/" />;
      case 404:
        return <Redirect302 to={checkout.checkoutPath} />;
      default:
        return <ErrorHandler {...props} />;
    }
  };

  getContent = () => {
    const { orderStore } = this.props;
    const { currentOrder } = orderStore;
    const paymentMethodMessages = currentOrder.payment_method_messages;
    return currentOrder.html_widget ? (
      <CustomCol xs={12} md={10} xxl={7}>
        <div className="ThankYouPageContent__payment-method-messages">
          {paymentMethodMessages &&
            paymentMethodMessages.length > 0 &&
            paymentMethodMessages.map((message, index) => (
              <Alert key={index} color="warning">
                <span>{message}</span>
              </Alert>
            )
            )}
        </div>
        {this.renderHtmlWidget(currentOrder.html_widget)}
      </CustomCol>
    ) : (
      <CheckoutOrderConfirmation />
    );
  };

  renderBannerContent = () => {
    const searchParams = {
      bannerZone: AdZones.ORDER_CONFIRMATION_CONTENT
    };

    return (
      <div className="ThankYouPageContent__banner-container">
        <ContentSlots searchParams={searchParams} />
      </div>
    );
  };

  renderReceiptUrl = () => {
    const { currentOrder } = this.props.orderStore;
    if (!currentOrder) {
      return null;
    }

    return (
      <div>
        {currentOrder.receipt_url && (
          <Button
            tag="a"
            href={currentOrder.receipt_url}
            target="_blank"
            rel="nofollow noopener"
            color="primary"
            className="ThankYouPageContent__receipt-button"
          >
            <Icon name="print" />{' '}
            <FormattedMessage
              id="checkout.successfulPurchaseReceipt"
              defaultMessage="Receipt"
            />
          </Button>
        )}
      </div>
    );
  };

  renderSuccessPage = () => (
    <Fragment>
      <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
      <Row className="ThankYouPageContent">
        <CustomCol xs={12} className="ThankYouPageContent__order-confirmation">
          <PageTitle>
            <FormattedMessage
              id="checkout.successfulPurchasePageHeading"
              defaultMessage="Order confirmation"
            />
          </PageTitle>
        </CustomCol>
        {this.getContent()}
        <CustomCol xs={12} md={10} xxl={7}>
          {this.renderBannerContent()}
          {this.renderReceiptUrl()}
          <div className="ThankYouPageContent__back-to-store-button">
            <Button tag={Link} to="/" color="secondary">
              <FormattedMessage
                id="checkout.backToStore"
                defaultMessage="Back to the store"
              />
            </Button>
          </div>
        </CustomCol>
      </Row>
      {this.state.renderTrackingPixels && <TrackingPixels />}
      {this.state.renderTrackingPixels && <ShopAlike />}
    </Fragment>
  );

  render() {
    return (
      <div className="ThankYouPage">
        <ContentForState
          state={this.getCurrentlyRunningRequestState()}
          forLoaded={this.renderSuccessPage}
          error={this.getError()}
          errorHandler={this.renderErrorHandler}
        />
      </div>
    );
  }
}

ThankYouPageContent.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  location: RouterPropTypes.location.isRequired,
  onOrderSuccess: PropTypes.func
};

export default injectIntl(inject(
  'configStore',
  'currencyStore',
  'orderStore',
  'productStore',
  'analytics'
)(withRouter(ThankYouPageContent)));



// WEBPACK FOOTER //
// ./src/components/checkout/ThankYouPageContent/index.jsx